// src/components/NotificationBell.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Clock, User, MapPin } from 'lucide-react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';

const NotificationBell = ({ restaurantId, onNavigateToOrders }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasNewOrder, setHasNewOrder] = useState(false);
  const dropdownRef = useRef(null);
  const prevCountRef = useRef(0);

  // 🔔 Listen for new pending orders
  useEffect(() => {
    if (!restaurantId) return;

    const ordersRef = collection(db, 'restaurants', restaurantId, 'orders');
    const q = query(ordersRef, where('status', '==', 'Pending'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orders = [];
      snapshot.forEach((doc) => orders.push({ id: doc.id, ...doc.data() }));

      orders.sort((a, b) => new Date(b.time) - new Date(a.time));

      if (orders.length > prevCountRef.current && prevCountRef.current > 0) {
        setHasNewOrder(true);
        playNotificationSound(); // 🔊 Play triple alert
        setTimeout(() => setHasNewOrder(false), 3000);
      }

      prevCountRef.current = orders.length;
      setPendingOrders(orders);
    });

    return () => unsubscribe();
  }, [restaurantId]);

  // 🔕 Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔊 Play sound 3 times
  const playNotificationSound = () => {
    try {
      const play = (count = 0) => {
        if (count >= 3) return;
        const audio = new Audio(
          'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGS57OihUBAKT6Ll8LdjHAU7k9jyzHksBSh+zPDckj8JE1y06+qnVhMKRp/g8r5sIQUqgc3y2og2CBhku+zooVAQCk+j5fC2YxwFO5LY8sx5LAUofszw3ZI/CRNctOvqp1YTCkaf4PK+bCEFKoHN8tqINggYZLvs6KFQEApPo+XwtmMcBTuS2PLMeSwFKH7M8N2SPwkTXLTr6qdWEwpGn+DyvmwhBSqBzfLaiDYIGGS77OihUBAKT6Pl8LZjHAU7k9jyzHksBSh+zPDdkj8JE1y06+qnVhMKRp/g8r5sIQUqgc3y2og2CBhku+zooVAQCk+j5fC2YxwFO5LY8sx5LAUofszw3ZI/CRNctOvqp1YTCkaf4PK+bCEFKoHN8tqINggYZLvs6KFQEApPo+XwtmMcBTuS2PLMeSwFKH7M8N2SPwkTXLTr6qdWEwpGn+DyvmwhBSqBzfLaiDYIGGS77OihUBAKT6Pl8LZjHAU7ktjyzHksBSh+zPDdkj8JE1y06+qnVhMK'
        );
        audio.volume = 1.0;
        audio.play().catch((e) => console.log('Audio play failed:', e));
        setTimeout(() => play(count + 1), 1000); // repeat after 1s
      };
      play();
    } catch (error) {
      console.log('Audio notification failed:', error);
    }
  };

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
    setHasNewOrder(false);
  };

  const handleOrderClick = (orderId) => {
    setShowDropdown(false);
    onNavigateToOrders();
  };

  const handleViewAllOrders = () => {
    setShowDropdown(false);
    onNavigateToOrders();
  };

  const pendingCount = pendingOrders.length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 🔔 Bell Button */}
      <button
        onClick={handleBellClick}
        className={`relative p-2 rounded-lg transition-all ${
          pendingCount > 0
            ? 'text-orange-500 hover:bg-orange-100'
            : 'text-gray-500 hover:bg-gray-100'
        } ${hasNewOrder ? 'animate-bounce' : ''}`}
        aria-label={`${pendingCount} pending orders`}
      >
        <Bell
          className={`w-6 h-6 ${
            hasNewOrder ? 'animate-pulse text-orange-600' : 'text-orange-500'
          }`}
        />

        {/* 🔴 Counter Badge */}
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 shadow-md animate-pulse">
            {pendingCount > 99 ? '99+' : pendingCount}
          </span>
        )}
      </button>

      {/* 🔽 Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[70vh] overflow-hidden flex flex-col">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">Pending Orders</h3>
              <p className="text-xs text-orange-100">
                {pendingCount} order{pendingCount !== 1 ? 's' : ''} waiting
              </p>
            </div>
            <button
              onClick={() => setShowDropdown(false)}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 🧾 Orders */}
          <div className="overflow-y-auto flex-1">
            {pendingCount > 0 ? (
              <div className="divide-y divide-gray-100">
                {pendingOrders.map((order) => (
                  <div
                    key={order.id}
                    onClick={() => handleOrderClick(order.id)}
                    className="p-4 hover:bg-orange-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-800 text-sm mb-1">
                          Order #{order.id}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                          <Clock className="w-3 h-3" />
                          <span>{new Date(order.time).toLocaleTimeString()}</span>
                        </div>
                        {order.customerName && (
                          <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                            <User className="w-3 h-3" />
                            <span className="truncate">{order.customerName}</span>
                          </div>
                        )}
                        {order.tableName && (
                          <div className="flex items-center gap-2 text-xs text-orange-600 font-medium">
                            <MapPin className="w-3 h-3" />
                            <span>{order.tableName}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-2 flex-shrink-0">
                        <p className="font-bold text-orange-600 text-lg">₹{order.total}</p>
                        <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                          Pending
                        </span>
                      </div>
                    </div>

                    {order.itemDetails && order.itemDetails.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Items:</p>
                        <div className="text-xs text-gray-700">
                          {order.itemDetails.slice(0, 2).map((item, idx) => (
                            <div key={idx} className="truncate">
                              • {item.name} × {item.quantity}
                            </div>
                          ))}
                          {order.itemDetails.length > 2 && (
                            <div className="text-gray-500 italic">
                              +{order.itemDetails.length - 2} more items
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No pending orders</p>
              </div>
            )}
          </div>

          {pendingCount > 0 && (
            <div className="p-3 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleViewAllOrders}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 rounded-lg hover:opacity-90 font-semibold text-sm transition"
              >
                View All Orders
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
